import { useEffect, useState } from 'react';
import { PencilIcon, TrashIcon, PlusIcon } from '@bizfirst/common-react';
import type { InstructionDTO, InstructionFormValues } from '../../types';
import { useAgentAdminApiClient } from '../../context/AiAgentAdminApiContext';
import { ConfirmDialog } from '../Shared/ConfirmDialog';

const EMPTY_FORM: InstructionFormValues = { Name: '', Description: '', Content: '', InstructionTypeID: 1, IsActive: true };

/**
 * The one AgentEditor sub-entity tab brought into v1 — the only one confirmed to have real,
 * fully-wired CRUD (including working delete) in the source editor. See AgentEditor.tsx's doc
 * comment for why the other ~9 sub-entity tabs were deferred.
 */
export function InstructionsPanel({ agentId }: { agentId: number }) {
  const api = useAgentAdminApiClient();
  const [instructions, setInstructions] = useState<InstructionDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<InstructionDTO | 'new' | null>(null);
  const [form, setForm] = useState<InstructionFormValues>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<InstructionDTO | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    const result = await api.getInstructionsByAgent(agentId);
    setInstructions(result.data ?? []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  const startEdit = (instruction: InstructionDTO | 'new') => {
    setEditing(instruction);
    setForm(
      instruction === 'new'
        ? EMPTY_FORM
        : { Name: instruction.Name, Description: instruction.Description ?? '', Content: instruction.Content, InstructionTypeID: instruction.InstructionTypeID, IsActive: instruction.IsActive }
    );
  };

  const handleSave = async () => {
    if (!form.Name.trim() || !form.Content.trim()) return;
    setSaving(true);
    const result = editing === 'new' ? await api.createInstruction(agentId, form) : await api.updateInstruction((editing as InstructionDTO).InstructionID, form);
    setSaving(false);
    if (result.success) {
      setEditing(null);
      await load();
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    const result = await api.deleteInstruction(pendingDelete.InstructionID);
    setDeleting(false);
    if (result.success) {
      setPendingDelete(null);
      setInstructions((prev) => prev.filter((i) => i.InstructionID !== pendingDelete.InstructionID));
    }
  };

  if (editing) {
    return (
      <div className="aaa-form-grid">
        <div className="aaa-form-group aaa-form-group-full">
          <label>Name *</label>
          <input type="text" value={form.Name} onChange={(e) => setForm((f) => ({ ...f, Name: e.target.value }))} />
        </div>
        <div className="aaa-form-group aaa-form-group-full">
          <label>Description</label>
          <input type="text" value={form.Description ?? ''} onChange={(e) => setForm((f) => ({ ...f, Description: e.target.value }))} />
        </div>
        <div className="aaa-form-group aaa-form-group-full">
          <label>Content *</label>
          <textarea rows={8} value={form.Content} onChange={(e) => setForm((f) => ({ ...f, Content: e.target.value }))} />
        </div>
        <div className="aaa-form-group">
          <label className="aaa-checkbox">
            <input type="checkbox" checked={form.IsActive} onChange={(e) => setForm((f) => ({ ...f, IsActive: e.target.checked }))} />
            Active
          </label>
        </div>
        <div className="aaa-form-group-full aaa-editor-footer-actions">
          <button type="button" className="aaa-btn aaa-btn-default" onClick={() => setEditing(null)} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="aaa-btn aaa-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Instruction'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="aaa-editor-footer-actions" style={{ marginBottom: 12 }}>
        <button type="button" className="aaa-btn aaa-btn-primary aaa-btn-sm" onClick={() => startEdit('new')}>
          <PlusIcon size={13} /> Add Instruction
        </button>
      </div>
      {loading && <div className="aaa-editor-loading">Loading…</div>}
      {!loading && instructions.length === 0 && <div className="aaa-entity-dropdown-empty">No instructions yet.</div>}
      <div className="aaa-entity-grid">
        {instructions.map((instruction) => (
          <div key={instruction.InstructionID} className="aaa-entity-card">
            <div className="aaa-entity-card-header">
              <strong>{instruction.Name}</strong>
              <span className={`aaa-badge ${instruction.IsActive ? 'aaa-badge-active' : 'aaa-badge-inactive'}`}>
                {instruction.IsActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            {instruction.Description && <p>{instruction.Description}</p>}
            <div className="aaa-entity-card-actions">
              <button type="button" className="aaa-icon-btn" onClick={() => startEdit(instruction)} title="Edit">
                <PencilIcon size={13} />
              </button>
              <button type="button" className="aaa-icon-btn aaa-icon-btn-danger" onClick={() => setPendingDelete(instruction)} title="Delete">
                <TrashIcon size={13} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <ConfirmDialog
        isOpen={!!pendingDelete}
        title="Delete Instruction"
        message={`Delete instruction "${pendingDelete?.Name}"?`}
        busy={deleting}
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
